import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UrlService } from '../../../../environments/urlService';
import { IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import {
  BoxAccessPropsDTO, BoxEmailDTO, BoxEmailDTORs,
  BoxFolderType,
  BoxItemInfoWrapper, BoxMoveItemsWrapper,
  BoxNodeInfo,
  BoxNotificationWrapper, DetailedBoxNodeInfo,
} from '../dto/BoxDTOWrapper';
import { tap } from 'rxjs/operators';
import {
  ActivityLogFilter,
  BoxDataRoomExternalInfo,
  DataRoomAdminWrapperDTO,
  DataRoomFolderCreator,
  DataRoomFolderNameEditor,
  DataRoomFolderNameEraser,
  DataRoomInvitation,
  DataRoomPublishToFund,
  DataRoomRenameExtUser,
  DataRoomTokens,
  DataRoomExternalUserAccesses,
  DRExternalUserRq,
  ExternalUserDenyAccess,
  FilteredUserLogWrapper,
  LogData,
  DRCopyFolderRq,
  DataRoomUnpublishFromFund,
  DataRoomFolderUserRoleChanger,
  RefreshBoxToken,
  DataRoomNotificationChanger,
} from "../component/data-room/data-room.util";
import { BoxKpiAccessAndNodesWrapper } from "../../kpi/modals/kpi-modal-document-crud/kpi-modal-document-crud.component";
import { CreatorFolderForMetricDataRs, FileDeletionMetricData, KpiSharedLinkParams } from "../../kpi/dto/box/box-kpi-dtos";
import { BoxItem } from "./box-file-structure.service";
import { BoxUrls } from "../component/box.util";

@Injectable({
  providedIn: 'root',
})
export class BoxServiceService {
  private readonly urls: BoxUrls;
  private readonly DATA_ROOM_HEADER: string = 'Data-Room-Token';
  private _selectedNodeInTree = new BehaviorSubject<BoxNodeInfo>(undefined);
  selectedNodeInTree$: Observable<BoxNodeInfo>;
  private notificationTrigger = new Subject<BoxNotificationWrapper>();
  private moveSubject = new BehaviorSubject<boolean>(false);
  moveSubject$ = this.moveSubject.asObservable();
  private _organizationPickerIsBlocked = new BehaviorSubject<boolean>(false); // block org picker and show button back
  organizationPickerIsBlocked = this._organizationPickerIsBlocked.asObservable();
  private _updateFlag = new BehaviorSubject<void>(undefined);
  /**
   * An observable that emits a void value whenever an update occurs, serving as a simple notifier
   * for cases where multiple box components need to synchronize their state.
   * For example, when one component updates files/folders, other components
   * subscribed to this flag can react and refresh their data.
   */
  updateFlag$ = this._updateFlag.asObservable();

  constructor(private httpClient: HttpClient, private urlService: UrlService) {
    this.urls = new BoxUrls(this.urlService.getFmsUrl(), this.urlService.getPortalUrl(), this.urlService.getExternalDataRoomUrl());
    this.selectedNodeInTree$ = this._selectedNodeInTree.asObservable();
    this.notificationTrigger.subscribe((notification) => {
      this.doNotification(notification).subscribe();
    });
  }

  setIfOrganizationPickerIsBlocked(showBack: boolean): void {
    this._organizationPickerIsBlocked.next(showBack);
  }

  triggerNotification(notification: BoxNotificationWrapper): void {
    this.notificationTrigger.next(notification);
  }

  emitEventForMove(data: boolean): void {
    this.moveSubject.next(data);
  }

  toggleSelectedTreeFolder(value: BoxNodeInfo): void {
    this._selectedNodeInTree.next(value);
  }

  /**
   * Triggers an update by emitting a void value on the `_updateFlag` subject.
   * This method is used to notify subscribers (e.g., other components) that
   * an update has occurred, such as when files or folders are modified.
   *
   * Use case: When two box components are loaded simultaneously, and one of them
   * updates files/folders, the other component can subscribe to `updateFlag$`
   * and react to changes accordingly.
   */
  triggerUpdate(): void {
    this._updateFlag.next();
  }

  getBoxFolder(uuid: string, type: BoxFolderType): Observable<BoxAccessPropsDTO> {
    return this.httpClient.get<BoxAccessPropsDTO>(this.urls.fmsBox + 'access-props',
      {
        params: { type, uuid },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  downloadMultipleFiles(items: BoxItem[], isFromDataAdminRoom: boolean): Observable<string> {
    const wrapper: BoxItemInfoWrapper = {
      items: items,
      isFromDataAdminRoom: isFromDataAdminRoom,
    };
    return this.httpClient.post(this.urls.fmsBox + 'download', wrapper, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      responseType: 'text',
    });
  }

  downloadMultipleFilesFromBoxApi(items: BoxItem[], tokens: DataRoomTokens, isExternal: boolean): Observable<string> {
    if (isExternal) {
      return this.httpClient.post(this.urls.externalUserDataRoom + 'zip', items, {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        responseType: 'text',
      });
    }
    const headers = new HttpHeaders({
      [this.DATA_ROOM_HEADER]: tokens.userToken,
    });
    return this.httpClient.post(this.urls.portalUserDataRoom + 'zip', items, {
      headers,
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      responseType: 'text',
    });

  }

  sendLogData(data: LogData, portalToken: string, isExternal: boolean): Observable<void> {
    if (isExternal) {
      return this.httpClient.post<void>(this.urls.externalUserDataRoom + 'log', data, {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      });
    }
    const headers = new HttpHeaders({
      [this.DATA_ROOM_HEADER]: portalToken,
    });
    return this.httpClient.post<void>(this.urls.portalUserDataRoom + 'log', data, {
      headers,
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });

  }

  sendMagicLinkWithCaptcha(email: string, captchaV2Token: string, captchaV3Token: string): Observable<BoxEmailDTORs> {
    const body: BoxEmailDTO = { email, captchaV2Token, captchaV3Token };
    return this.httpClient.post<BoxEmailDTORs>(this.urls.externalUserDataRoomAuth + 'email-authentication', body, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  externalLoginByToken(token: string): Observable<void> {
    return this.httpClient.get<void>(this.urls.externalUserDataRoomAuth + 'login', {
      params: { token },
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  getOrgsAndFolders(): Observable<DataRoomExternalUserAccesses> {
    return this.httpClient.get<DataRoomExternalUserAccesses>(this.urls.externalUserDataRoom + 'access', {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  getTokenForExternalPersisted(data: DRExternalUserRq): Observable<BoxDataRoomExternalInfo> {
    return this.httpClient.post<BoxDataRoomExternalInfo>(this.urls.externalUserDataRoom + 'box-token', data, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  getNewBoxToken(rootFolderId: string): Observable<RefreshBoxToken> {
    return this.httpClient.get<RefreshBoxToken>(this.urls.externalUserDataRoom + 'refresh-box-token', {
      params: { rootFolderId },
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  logout(): Observable<void> {
    return this.httpClient.post<void>(this.urls.externalUserDataRoom + 'logout', null, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  deleteItems(items: BoxItem[]): Observable<void> {
    return this.httpClient.post<void>(this.urls.fmsBox + 'delete', items, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  moveItem(destinationFolder: BoxItem, selectedItems: BoxItem[]): Observable<void> {
    const body: BoxMoveItemsWrapper = { selectedItems, rootOrDestinationFolder: destinationFolder };
    return this.httpClient.post<void>(this.urls.fmsBox + 'move', body, {
      params: null,
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  rename(item: BoxItem): Observable<void> {
    const boxRenameWrapper = {
      type: item.type,
      id: item.id,
      newName: item.name,
    };
    return this.httpClient.post<void>(this.urls.fmsBox + 'rename', boxRenameWrapper, {
      params: null,
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  copy(items: BoxItem[]): Observable<void> {
    return this.httpClient.post<void>(this.urls.fmsBox + 'copy', items, {
      params: null,
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  /**
   * Retrieves the child nodes of a specified root folder.
   *
   * The response from this endpoint should be an array of `BoxNodeInfo` objects.
   * The titles of all returned nodes are formatted by calling `formatNodeTitle()` method.
   *
   * @param selectedItems An array of `BoxItem` objects to be moved.
   * @param rootFolder The `BoxItem` object representing the root or destination folder.
   * @returns An Observable that emits an array of `BoxNodeInfo` objects representing the child nodes of the folder.
   */
  getOrgNodes(selectedItems: BoxItem[], rootFolder: BoxItem): Observable<BoxNodeInfo[]> {
    const body: BoxMoveItemsWrapper = { selectedItems, rootOrDestinationFolder: rootFolder };
    return this.httpClient.post<BoxNodeInfo[]>(this.urls.fmsBox + 'inner-nodes', body,
      {
        params: null,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true,
      }).pipe(tap((nodes) => nodes.forEach((node) => {
      this.formatNodeTitle(node);
    })));
  }

  /**
   * Gets the inner structure of a specified folder recursively.
   *
   * The response from this endpoint should be an array of `DetailedBoxNodeInfo` objects.
   * The titles of all returned nodes are formatted by calling `formatNodeTitle()` method.
   *
   * @param selectedItems An array of `BoxItem` objects to be moved.
   * @param rootFolder The `BoxItem` object representing the root or destination folder.
   * @returns An Observable that emits an array of `DetailedBoxNodeInfo` objects representing the inner structure of the folder.
   */
  getInnerStructureOfFolderWithRecursive(selectedItems: BoxItem[],
    rootFolder: BoxItem): Observable<DetailedBoxNodeInfo[]> {
    const body: BoxMoveItemsWrapper = { selectedItems, rootOrDestinationFolder: rootFolder };
    return this.httpClient.post<DetailedBoxNodeInfo[]>(this.urls.fmsBox + 'inner-nodes-with-recursive', body,
      {
        params: null,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true,
      }).pipe(tap((nodes) => nodes.forEach((node) => {
      this.formatNodeTitle(node);
    })));
  }

  getBoxAccessTokenAndUserInfoForPortalDataRoomUser(token: string): Observable<BoxDataRoomExternalInfo> {
    const headers = new HttpHeaders({
      [this.DATA_ROOM_HEADER]: token,
    });
    return this.httpClient.get<BoxDataRoomExternalInfo>(this.urls.portalUserDataRoom + 'box-token',
      { headers,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false) });
  }

  doNotification(boxNotificationWrapper: BoxNotificationWrapper): Observable<void> {
    return this.httpClient.post<void>(this.urls.fmsBox + 'notification', boxNotificationWrapper, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
    });
  }

  initLogsAndOrgFolders(orgUUID: string): Observable<DataRoomAdminWrapperDTO> {
    return this.httpClient.get<DataRoomAdminWrapperDTO>(this.urls.adminDataRoom + 'init', {
      params: { orgUUID },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getFilteredActivityLogs(filter: ActivityLogFilter): Observable<FilteredUserLogWrapper> {
    return this.httpClient.post<FilteredUserLogWrapper>(this.urls.adminDataRoom + 'logs', filter, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  createDataRoomFolder(data: DataRoomFolderCreator): Observable<void> {
    return this.httpClient.post<void>(this.urls.adminDataRoom + 'folder', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  copyDataRoomFolder(data: DRCopyFolderRq): Observable<void> {
    return this.httpClient.post<void>(this.urls.adminDataRoom + 'copy-folder', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  renameDataRoomFolder(data: DataRoomFolderNameEditor): Observable<void> {
    return this.httpClient.post<void>(this.urls.adminDataRoom + 'rename-folder', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  deleteDataRoomFolder(data: DataRoomFolderNameEraser): Observable<void> {
    return this.httpClient.delete<void>(this.urls.adminDataRoom + 'delete-folder', {
      body: data,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  publishToFund(data: DataRoomPublishToFund): Observable<void> {
    return this.httpClient.post<void>(this.urls.adminDataRoom + 'publish-to-fund', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  unpublishFromFund(data: DataRoomUnpublishFromFund): Observable<void> {
    return this.httpClient.delete<void>(this.urls.adminDataRoom + 'unpublish-fund', {
      body: data,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  sendInvite(data: DataRoomInvitation): Observable<void> {
    return this.httpClient.post<void>(this.urls.adminDataRoom + 'invite-ext-user', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  changeRoleExistedUser(data: DataRoomFolderUserRoleChanger): Observable<void> {
    return this.httpClient.put<void>(this.urls.adminDataRoom + 'external-user-role', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  renameExtUserName(data: DataRoomRenameExtUser): Observable<void> {
    return this.httpClient.post<void>(this.urls.adminDataRoom + 'rename-ext-user', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  denyExtUserAccess(data: ExternalUserDenyAccess): Observable<void> {
    return this.httpClient.delete<void>(this.urls.adminDataRoom + 'deny-ext-user-access', {
      body: data,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  updateNotification(data: DataRoomNotificationChanger): Observable<void> {
    return this.httpClient.put<void>(this.urls.adminDataRoom + 'update-notification', data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  /**
   * Retrieves a simple structure (list of child nodes) within the specified Box folder.
   *
   * @param boxFolderId the unique identifier of the Box folder
   * @param maxLength maximal title length
   */
  getInnerSimpleStructureOfFolder(boxFolderId: string, maxLength?: number): Observable<DetailedBoxNodeInfo[]> {
    return this.httpClient.get<DetailedBoxNodeInfo[]>(this.urls.fmsBox + 'structure-folder',
      {
        params: { boxFolderId },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true,
      }).pipe(tap((nodes) => nodes.forEach((node) => {
      this.formatNodeTitle(node, maxLength);
    })));
  }

  getAccessPropsWithNodesKPI(companyUUID: string, metricDataUUID: string): Observable<BoxKpiAccessAndNodesWrapper> {
    return this.httpClient.get<BoxKpiAccessAndNodesWrapper>(this.urls.kpiBox + 'access-and-nodes',
      {
        params: { companyUUID, metricDataUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  getAccessPropsWithNodesKPISharedLink(companyUUID: string, metricDataUUID: string, token: string, password: string): Observable<BoxKpiAccessAndNodesWrapper> {
    const documentMetricDataRq = { metricDataUUID, companyUUID };
    const dto = { documentMetricDataRq, password, token };
    return this.httpClient.post<BoxKpiAccessAndNodesWrapper>(this.urls.kpiSharedBox + 'access-and-nodes', dto,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  downloadMultipleFilesKPI(companyUUID: string, metricDataUUID: string, sharedLinkParams?: KpiSharedLinkParams): Observable<string> {
    const documentMetricDataRq = { metricDataUUID, companyUUID };
    const dto = sharedLinkParams
      ? { documentMetricDataRq, password: sharedLinkParams.password, token: sharedLinkParams.token }
      : documentMetricDataRq;
    const endpoint = sharedLinkParams
      ? this.urls.kpiSharedBox + 'zip'
      : this.urls.kpiBox + 'zip';
    return this.httpClient.post(endpoint, dto, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      responseType: 'text',
    });
  }

  deleteItemFromMetricData(fileDeletionDto: FileDeletionMetricData, sharedLinkParams?: KpiSharedLinkParams) : Observable<void> {
    const dto = sharedLinkParams
      ? { fileDeletionDto, password: sharedLinkParams.password, token: sharedLinkParams.token }
      : fileDeletionDto;
    const endpoint = sharedLinkParams
      ? this.urls.kpiSharedBox + 'file/deletion'
      : this.urls.kpiBox + 'file/deletion';
    return this.httpClient.post<void>(endpoint, dto, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  createFolderForMetricData(metricDataUUID: string, companyUUID: string): Observable<CreatorFolderForMetricDataRs> {
    const dto = { metricDataUUID, companyUUID };
    return this.httpClient.post<CreatorFolderForMetricDataRs>(this.urls.kpiBox + 'creation', dto,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  createFolderForMetricDataSharedLink(metricDataUUID: string, companyUUID: string, password: string, token: string): Observable<CreatorFolderForMetricDataRs> {
    const documentMetricDataRq = { metricDataUUID, companyUUID };
    const dto = { documentMetricDataRq, password, token };
    return this.httpClient.post<CreatorFolderForMetricDataRs>(this.urls.kpiSharedBox + 'creation', dto,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  /**
   * Formats the title of a given node. If the title is longer than 25 characters, it is truncated.
   * For a folder or a file without an extension, the title is simply cut off and three dots are appended.
   * For a file with an extension, the title is cut off in such a way that the extension is kept in the truncated title.
   *
   * The method also formats the titles of all items in the 'data' field of the node (if present) by recursively calling itself.
   *
   * @param node The node for which the title should be formatted.
   * @param maxLength maximal title length
   */
  private formatNodeTitle(node: DetailedBoxNodeInfo, maxLength: number = 25): void {
    const title = node.title;
    if (title.length > maxLength) {
      const threeDots = '...';
      const fileExtensionRE = /(?:\.([^.]+))?$/;
      const fileExtension = fileExtensionRE.exec(title)[1];
      if (node.type === 'folder' || !fileExtension) {
        node.title = title.substring(0, maxLength - threeDots.length) + threeDots;
      } else {
        const fileExtensionSize = fileExtension.length;
        node.title = title.substring(0, maxLength - threeDots.length - fileExtensionSize) + threeDots + fileExtension;
      }
    }
    if (node.data) {
      node.data.forEach((item) => this.formatNodeTitle(item, maxLength));
    }
  }
}
